import React, { createContext, useEffect, useReducer, useState } from "react";

// third-party
import { Chance } from "chance";
import jwtDecode from "jwt-decode";

// reducer - state management
import { LOGIN, LOGOUT } from "store/reducers/actions";
import authReducer from "store/reducers/auth";

// project import
import Loader from "components/Loader";
import axios from "utils/axios";
import { KeyedObject } from "types/root";
import { AuthProps, JWTContextType, UserProfile } from "types/auth";
import { BASE_URL, USER_ROLE } from "utils/Constants";
import { decodeToken } from "utils/decodeToken";
import { useNavigate } from "react-router";

const chance = new Chance();

// constant
const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
  twoFactorAuth: false,
};

const verifyToken: (st: string) => boolean = (serviceToken) => {
  if (!serviceToken) {
    return false;
  }
  // return true;
  const decoded: KeyedObject = jwtDecode(serviceToken);
  /**
   * Property 'exp' does not exist on type '<T = unknown>(token: string, options?: JwtDecodeOptions | undefined) => T'.
   */
  return decoded.exp > Date.now() / 1000;
};

export const getUser: (st: string) => any = (serviceToken) => {
  if (!serviceToken) {
    return { _id: '', role: -1, expire: false };
  }
  const decoded: KeyedObject = jwtDecode(serviceToken);
  return { _id: decoded._id, role: decoded.role, expire: decoded.exp > Date.now() / 1000, iatdate: decoded.iat? new Date(decoded.iat * 1000).toISOString().split('T')[0]: '' }
}

const setSession = (serviceToken?: string | null) => {
  if (serviceToken) {
    localStorage.setItem("serviceToken", serviceToken);
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  } else {
    localStorage.removeItem("serviceToken");
    if (localStorage.getItem("old_token")) localStorage.removeItem("old_token");
    delete axios.defaults.headers.common.Authorization;
  }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext<JWTContextType | null>(null);

export const JWTProvider = ({ children }: { children: React.ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [timer, setTimer] = useState(0); // state to track interval value
  const navigate = useNavigate();
  const [userRegion, setUserRegion] = useState<string[]>([]);

  useEffect(() => {
    const init = async () => {
      try {
        const serviceToken = window.localStorage.getItem("serviceToken");
        if (serviceToken && verifyToken(serviceToken)) {
          setSession(serviceToken);
          const user = await decodeToken(serviceToken);
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user,
              twoFactorAuth: user.two_factor_enabled,
            },
          });
        } else {
          dispatch({
            type: LOGOUT,
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: LOGOUT,
        });
      }
    };

    init();
  }, []);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
  
    const checkUserStatus = async () => {
      const serviceToken = window.localStorage.getItem("serviceToken");
      const { _id, role, expire, iatDate } = getUser(serviceToken || '');
      if(iatDate < '2024-11-29' && role === USER_ROLE.USER){
        dispatch({ type: LOGOUT });
        return;
      }
      if (serviceToken && expire && _id && role === USER_ROLE.ADMIN) {
        setTimer((prevTimer) => prevTimer + 1);
        try {
          const response = await axios.get(`${BASE_URL}/api/auth/is-active/${_id}`);
          if (response.data) {
            const status = response.data.status;
            if (!status) {
              dispatch({ type: LOGOUT });
            }
          }
        } catch (error) {
          console.error("Error checking user status", error);
        }
      }
      if(role === USER_ROLE.ADMIN){
        startInterval()
      } else if(role === USER_ROLE.USER){
        getUserForRegion(_id);
      }
    };

    const getUserForRegion = async (userId: string) => {
      try{
        const response = await axios.get(`${BASE_URL}/api/auth/check-user-region/${userId}`);
        if (response.data) {
          const userData = response.data.user;
          setUserRegion(Array.isArray(userData.region) ? userData.region : [userData.region]);
        }
      }catch(err){
        setUserRegion([])
      }
    }
  
    // Handle visibility change (pause or resume check)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkUserStatus();  // Call it immediately when the tab becomes visible
      } else {
        clearInterval(intervalId); // Pause checking when the tab is hidden
      }
    };
  
    const handleFocus = () => {
      checkUserStatus();  // Call it immediately when the window/tab gains focus
    };
  
    // Function to start the interval for checking user status
    const startInterval = () => {
      clearInterval(intervalId);
      intervalId = setInterval(checkUserStatus, 5000);
    };
  
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
  
    // Start the initial interval
    checkUserStatus(); // Run immediately on mount
  
    // Cleanup on component unmount and remove event listeners
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const loginAsUser = async (userId: string, oldToken: string) => {
    const response = await axios.post(`${BASE_URL}/api/auth/login-as-user`, {
      user_id: userId,
    });
    localStorage.setItem("old_token", oldToken);
    const { token } = response.data.data;
    const user = await decodeToken(token);
    setUserRegion(Array.isArray(user.region) ? user.region : user.region ? [user.region] : []);
    const twoFactorAuth = false; //todo: need to remove this
    setSession(token);
    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user,
        twoFactorAuth,
      },
    });
    navigate("/stores");
  };

  const login = async (
    email: string,
    password: string,
    two_factor_code?: string
  ) => {
    const response = await axios.post(`${BASE_URL}/api/auth/login`, {
      email,
      password,
      two_factor_code,
    });

    const twoFactorAuth = response.data.data.two_factor_enabled;

    if (!twoFactorAuth) {
      const { token } = response.data.data;
      if (!token) {
        throw new Error(response.data.message);
      }
      const user = await decodeToken(token);
      setUserRegion(Array.isArray(user.region) ? user.region : user.region ? [user.region] : []);
      setSession(token);
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          twoFactorAuth,
          user,
        },
      });
    } else {
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: false,
          twoFactorAuth,
        },
      });
    }
  };

  const register = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ) => {
    // todo: this flow need to be recode as it not verified
    const id = chance.bb_pin();
    const response = await axios.post("/api/account/register", {
      id,
      email,
      password,
      firstName,
      lastName,
    });
    let users = response.data;

    if (
      window.localStorage.getItem("users") !== undefined &&
      window.localStorage.getItem("users") !== null
    ) {
      const localUsers = window.localStorage.getItem("users");
      users = [
        ...JSON.parse(localUsers!),
        {
          id,
          email,
          password,
          name: `${firstName} ${lastName}`,
        },
      ];
    }

    window.localStorage.setItem("users", JSON.stringify(users));
  };

  const logout = () => {
    setSession(null);
    dispatch({ type: LOGOUT });
  };

  const resetPassword = async (email: string) => {};

  const updateProfile = () => {};

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider
      value={{
        ...state,
        login,
        loginAsUser,
        logout,
        register,
        resetPassword,
        updateProfile,
        // addStore,
        // updateStore,
        userRegion
      }}
    >
      {children}
    </JWTContext.Provider>
  );
};

export default JWTContext;
